<template>
  <collapsible-section class='_pt0 _pb0'>
    <template #headerText>
      <p class='color-leaf'>Member Eligibility</p>
    </template>
    <template #headerActions>
      <template v-if="benefitCategories && benefitCategories.length > 0">
        <select class="native-filter" style="width: 50%;" v-model="activeBenefitCategoryIndex">
          <option
            id="benefitCategory"
            name="benefitCategory"
            v-for="(option, i) in benefitCategories"
            :selected="i === activeBenefitCategoryIndex"
            :key="i"
            :value="i"
          >
            {{option.name}}
          </option>
        </select>
        <label for="benefitCategory">Category:</label>
      </template>
    </template>
    <template #collapsibleSection>
      <async-loader v-if='memberDetailsLoading'/>
      <div container v-else>
        <div grid>
          <div column="m-6">
            <date-input
              v-model="dateOfService"
              label="Eligibility As Of:"
              inputId="eligibilityAsOf"
              @input="emitInternalValue"/>
          </div>
        </div>
        <div v-if="activeEligibleServices.length < 1">
          <div grid>
            <div column="m-6" class="color-moon not-found">
              Unable to find benefit information.  Please try again later.
            </div>
          </div>
        </div>
        <template v-else>
          <div v-for="(eligibleService, i) in activeEligibleServices" :key="i">
            <table class="member-benefits _mbs" v-if="eligibleService.inNetworkServiceRestrictions.length > 0">
              <thead>
                <th>In-Network Restrictions</th>
              </thead>
              <tbody>
                <tr v-for="(restriction, i) in eligibleService.inNetworkServiceRestrictions" :key="i">
                  <td>{{ restriction }}</td>
                </tr>
              </tbody>
            </table>
            <table class="member-benefits _mbs" v-if="eligibleService.outOfNetworkServiceRestrictions.length > 0">
              <thead>
                <th>Out-of-Network Restrictions</th>
              </thead>
              <tbody>
                <tr v-for="(restriction, i) in eligibleService.outOfNetworkServiceRestrictions" :key="i">
                  <td>{{ restriction }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <table class="member-benefits">
            <thead>
              <th>Service</th>
              <th>Frequency</th>
              <th>Eligible</th>
              <th>Eligible as of Date</th>
            </thead>
            <tbody
              v-if="activeEligibleServices"
              v-for="(eligibleService, i) in activeEligibleServices"
              :key='`eligible-service-${i}`'
            >
              <tr>
                <td colspan="4">
                  <strong>{{eligibleService.benefitCategory}}</strong>
                </td>
              </tr>
              <tr class='nested'
                v-for='(eligibleCategory,k) in eligibleService.eligibilityCategories'
                :key="`eligible-service-category-${k}`"
              >
                <td>
                  <p>{{eligibleCategory.service}}</p>
                </td>
                <td>
                  <p>{{eligibleCategory.serviceFrequency}}</p>
                </td>
                <td>
                  <p>{{ eligibleCategory.eligible == true ? 'Yes' : 'No' }}</p>
                </td>
                <td>
                  <p>{{ formatDate(eligibleCategory.nextEligibleDate) }}</p>
                </td>
              </tr>
            </tbody>
          </table>

          <table
            v-for="(amountLimit, i) in amountLimitCategories"
            :key="`amount-limit-table-${i}`"
            v-if="amountLimit && amountLimit.limitCategoryDesc"
            class="member-benefits _mts"
          >
            <thead>
            <tr>
              <th colspan="4">
                {{ amountLimit.limitCategoryDesc }}
              </th>
            </tr>
            </thead>
            <tbody v-if="computedLimits(amountLimit).inNetwork.length">
              <tr>
                <td colspan="4">
                  <strong>In Network</strong>
                </td>
              </tr>
              <tr
                class="nested"
                v-for="(limit, i) in computedLimits(amountLimit).inNetwork"
                :key='`product-category-${i}`'
              >
                <td colspan="3">
                  <p>{{ limit.amountLimitDesc }}</p>
                </td>
                <td colspan="1">
                  <p>{{ limit.formattedStartAmount }}</p>
                </td>
              </tr>
            </tbody>
            <tbody v-if="computedLimits(amountLimit).outOfNetwork.length">
              <tr>
                <td colspan="4">
                  <strong>Out of Network</strong>
                </td>
              </tr>
              <tr
                class="nested"
                v-for="(limit, i) in computedLimits(amountLimit).outOfNetwork"
                :key='`product-category-${i}`'
              >
                <td colspan="3">
                  <p>{{ limit.amountLimitDesc }}</p>
                </td>
                <td colspan="1">
                  <p>{{ limit.formattedStartAmount }}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>
  </collapsible-section>
</template>

<script>
  import CollapsibleSection from '@sections/CollapsibleSection'
  import memberManagement from '@mixins/memberManagement'
  import formattable from '@mixins/formattable'
  import DateInput from '@atoms/DateInput'
  import { mapFields } from 'vuex-map-fields'
  import benefitsSummaryAndEligibility from '@mixins/benefitsSummaryAndEligibility'

  export default {
    mixins: [memberManagement, formattable, benefitsSummaryAndEligibility],
    components: {
      DateInput,
      CollapsibleSection
    },
    data() {
      return {
        dateOfService: ``,
      }
    },
    methods: {
      emitInternalValue() {
        this.$router.push({
          name: 'member-details',
          params: { memberCk: this.$hiddenRouteParams.memberCk },
          query: { dateOfService: this.dateOfService }
        })
      }
    },
    computed: {
      // ...mapFields([
      //   'queryStringHelper.queryParams.dateOfService'
      // ]),
      amountLimitCategories () {
        return Object.keys(this.memberDetails.amountLimit).map(key => this.memberDetails.amountLimit[key])
      },

      computedLimits () {
        return (amountLimitCategory) => {
          let limits = {
            'inNetwork': [],
            'outOfNetwork': []
          }
          try {
            let categoryLimits =
              amountLimitCategory
                .benefitCategoryLimits
                .find(benefitCategoryLimit => benefitCategoryLimit.benefitCategoryName === this.activeBenefitCategory.name)
                .limits

            categoryLimits.forEach(limit => {
              limit.formattedStartAmount = this.toDollarFormat(limit.startAmount)

              if (limit && limit.applicableNetworkType === 'I') {
                limits['inNetwork'].push(limit)
              } else if (limit && limit.applicableNetworkType === 'O') {
                limits['outOfNetwork'].push(limit)
              } else if (limit && limit.applicableNetworkType === 'B') {
                limits['inNetwork'].push(limit)
                limits['outOfNetwork'].push(limit)
              }
            })
          } catch (error) {
            console.error(error)
          }

          return limits
        }
      },

      activeEligibleServices () {
        try {
          return this.memberEligibility
            .filter(eligibleService => eligibleService.benefitCategory === this.activeBenefitCategory.name)
            .map(eligibleService => {
              eligibleService.eligibilityCategories = eligibleService.eligibilityCategories.sort((a, b) => {
                return a.sortOrder - b.sortOrder
              })
              return eligibleService
            })
        } catch (error) {
          console.error(error)
          return []
        }
      }
    }
  }
</script>

<style lang="css" scoped>
.not-found {
  margin-left: 2rem;
  padding-bottom: 1rem;
}
</style>
